/**
 * DUALHEAT™ High-Converting Single Product Storefront Logic
 * Vanilla JS • Zero External Dependencies • Native Shopify AJAX Cart API
 */

(function () {
  'use strict';

  // State Management
  const state = {
    selectedTier: 2, // 1: Solo, 2: Couples (Default), 3: Family Collector
    // Tier 1 Solo style state (Default: Midnight Black Bulldog)
    soloStyleName: 'Midnight Black (Bulldog)',
    soloStyleId: '',
    soloStyleImage: '',
    // Tier 2 Couples Pack style state (Default: Pair 1 = Midnight Black Bulldog, Pair 2 = Arctic White Bulldog)
    tier2Pair1Name: 'Midnight Black (Bulldog)',
    tier2Pair1Id: '',
    tier2Pair1Image: '',
    tier2Pair2Name: 'Arctic White (Bulldog)',
    tier2Pair2Id: '',
    tier2Pair2Image: '',
    // Fallback/shared
    allVariantIds: [],
    allVariants: [],
    realProduct: null,
    tierPricing: {
      1: {
        price: '$44.99',
        label: 'Add to Cart — $44.99',
        stickyName: 'Solo Explorer (1 Pair)',
        stickyPrice: '$44.99',
        stickyBadge: ''
      },
      2: {
        price: '$79.99',
        label: 'Add to Cart — $79.99 (Save $10)',
        stickyName: 'Couples Pack (2 Pairs)',
        stickyPrice: '$79.99',
        stickyBadge: 'Save $10'
      },
      3: {
        price: '$179.99',
        label: 'Add to Cart — $179.99 (Save $45)',
        stickyName: 'Family Collector Set',
        stickyPrice: '$179.99',
        stickyBadge: 'Save $45'
      }
    }
  };

  document.addEventListener('DOMContentLoaded', initDualHeatPDP);

  async function initDualHeatPDP() {
    await resolveRealStoreProduct();
    initGallery();
    initStyleChips();
    initQuantityBreaks();
    initStickyBar();
    initBannerTrigger();
    initCartAddAction();
  }

  /* ==========================================================================
     0. DYNAMIC SHOPIFY PRODUCT & REAL VARIANT RESOLUTION
     ========================================================================== */
  async function resolveRealStoreProduct() {
    let product = null;

    // 1. Check embedded Liquid product data
    const scriptEl = document.getElementById('dualheat-product-data');
    if (scriptEl && scriptEl.textContent.trim() && scriptEl.textContent.trim() !== 'null') {
      try {
        product = JSON.parse(scriptEl.textContent.trim());
      } catch (e) {
        console.warn('Could not parse dualheat-product-data script:', e);
      }
    }

    // 2. If not embedded, fetch store products dynamically via public Shopify storefront endpoint
    if (!product || !product.variants || product.variants.length === 0) {
      try {
        const res = await fetch('/products.json?limit=25');
        if (res.ok) {
          const data = await res.json();
          if (data && data.products && data.products.length > 0) {
            // Find DualHeat product or take the first active store product
            product = data.products.find(function (p) {
              return /dualheat/i.test(p.title) || /dualheat/i.test(p.handle);
            }) || data.products[0];
          }
        }
      } catch (err) {
        console.warn('Could not fetch store products:', err);
      }
    }

    // 3. Fallback: Check existing DOM chip data-variant-id attributes
    if (!product || !product.variants || product.variants.length === 0) {
      const chipBtns = document.querySelectorAll('.pdp-style-chip');
      const foundIds = [];
      chipBtns.forEach(function (btn) {
        const id = btn.getAttribute('data-variant-id');
        if (id && !id.startsWith('4501') && id.length >= 6 && !foundIds.includes(id)) {
          foundIds.push(id);
        }
      });
      if (foundIds.length > 0) {
        state.allVariantIds = foundIds;
        state.soloStyleId = foundIds[0];
        state.tier2Pair1Id = foundIds[0];
        state.tier2Pair2Id = foundIds[1] || foundIds[0];
        return;
      }
    }

    // 4. Bind real variants to state and DOM chips
    if (product && product.variants && product.variants.length > 0) {
      const vars = product.variants;
      state.realProduct = product;
      state.allVariants = vars;
      state.allVariantIds = vars.map(function (v) { return v.id.toString(); });

      // Helper function to match a style edition name to variant
      function findVariantForStyle(styleName, fallbackIdx) {
        const lower = (styleName || '').toLowerCase();
        const found = vars.find(function (v) {
          const t = ((v.title || '') + ' ' + (v.option1 || '') + ' ' + (v.option2 || '')).toLowerCase();
          if (lower.includes('peach') && t.includes('peach')) return true;
          if (lower.includes('bulldog') && t.includes('bulldog')) {
            if (lower.includes('black') && (t.includes('black') || t.includes('midnight'))) return true;
            if (lower.includes('white') && (t.includes('white') || t.includes('arctic'))) return true;
          }
          if (lower.includes('bear') && t.includes('bear')) {
            if (lower.includes('black') && (t.includes('black') || t.includes('midnight'))) return true;
            if (lower.includes('white') && (t.includes('white') || t.includes('arctic'))) return true;
          }
          if (t.includes(lower) || lower.includes(t)) return true;
          return false;
        });
        return found ? found.id.toString() : (vars[fallbackIdx] ? vars[fallbackIdx].id.toString() : vars[0].id.toString());
      }

      // Update data-variant-id on all style chips
      const allChips = document.querySelectorAll('.pdp-style-chip');
      allChips.forEach(function (chip, idx) {
        const styleName = chip.dataset.styleName || '';
        const fallbackIdx = idx % 5;
        const matchedId = findVariantForStyle(styleName, fallbackIdx);
        chip.setAttribute('data-variant-id', matchedId);
      });

      // Populate default IDs
      state.soloStyleId = findVariantForStyle(state.soloStyleName, 0);
      state.tier2Pair1Id = findVariantForStyle(state.tier2Pair1Name, 0);
      state.tier2Pair2Id = findVariantForStyle(state.tier2Pair2Name, 1);

      // Bind to native form hidden input
      const nativeInput = document.getElementById('dualheatNativeVariantId');
      if (nativeInput) {
        nativeInput.value = state.tier2Pair1Id;
      }
    }
  }

  /* ==========================================================================
     1. MEDIA GALLERY: SCROLL-SNAP & THUMBNAILS
     ========================================================================== */
  function initGallery() {
    const viewport = document.getElementById('pdpGalleryViewport');
    const dots = document.querySelectorAll('.pdp-gallery-dot');
    const thumbs = document.querySelectorAll('.pdp-thumb-item');

    if (!viewport) return;

    // Synchronize indicators on scroll
    viewport.addEventListener('scroll', function () {
      const scrollPos = viewport.scrollLeft;
      const slideWidth = viewport.offsetWidth || 1;
      const activeIndex = Math.round(scrollPos / slideWidth);

      dots.forEach(function (dot, idx) {
        dot.classList.toggle('active', idx === activeIndex);
      });
      thumbs.forEach(function (thumb, idx) {
        thumb.classList.toggle('active', idx === activeIndex);
      });
    }, { passive: true });

    // Click dot navigation
    dots.forEach(function (dot) {
      dot.addEventListener('click', function (e) {
        const targetIdx = parseInt(e.currentTarget.dataset.targetSlide || '0', 10);
        scrollToSlide(targetIdx);
      });
    });

    // Click thumbnail navigation
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function (e) {
        const targetIdx = parseInt(e.currentTarget.dataset.targetSlide || '0', 10);
        scrollToSlide(targetIdx);
      });
    });

    function scrollToSlide(index) {
      const slideWidth = viewport.offsetWidth;
      viewport.scrollTo({
        left: index * slideWidth,
        behavior: 'smooth'
      });
    }
  }

  /* ==========================================================================
     2. COMPACT STYLE CHIPS (SOLO & COUPLES)
     ========================================================================== */
  function initStyleChips() {
    const chips = document.querySelectorAll('.pdp-style-chip');
    const heroActiveImg = document.getElementById('pdpHeroActiveImage');
    const heroActiveThumb = document.getElementById('pdpHeroActiveThumb');
    const stickyActiveImg = document.getElementById('stickyActiveImg');
    const viewport = document.getElementById('pdpGalleryViewport');

    chips.forEach(function (chip) {
      chip.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();

        const group = chip.dataset.group; // 'solo', 'pair1', or 'pair2'
        const styleName = chip.dataset.styleName || '';
        const styleImg = chip.dataset.styleImage || '';
        const variantId = chip.dataset.variantId || '';

        // Determine which tier this chip belongs to and make sure it is selected
        const tier = (group === 'pair1' || group === 'pair2') ? 2 : 1;
        if (state.selectedTier !== tier) {
          selectTier(tier);
        }

        // Toggle active chip in this group
        const groupChips = document.querySelectorAll('.pdp-style-chip[data-group="' + group + '"]');
        groupChips.forEach(function (c) {
          c.classList.remove('active');
          c.setAttribute('aria-checked', 'false');
        });
        chip.classList.add('active');
        chip.setAttribute('aria-checked', 'true');

        // Update state
        if (group === 'solo') {
          state.soloStyleName = styleName;
          state.soloStyleImage = styleImg;
          if (variantId) state.soloStyleId = variantId;
        } else if (group === 'pair1') {
          state.tier2Pair1Name = styleName;
          state.tier2Pair1Image = styleImg;
          if (variantId) state.tier2Pair1Id = variantId;
        } else if (group === 'pair2') {
          state.tier2Pair2Name = styleName;
          state.tier2Pair2Image = styleImg;
          if (variantId) state.tier2Pair2Id = variantId;
        }

        // Update native form input
        const nativeInput = document.getElementById('dualheatNativeVariantId');
        if (nativeInput) {
          nativeInput.value = (state.selectedTier === 1 ? state.soloStyleId : state.tier2Pair1Id);
        }

        // Smooth swap of slide 1 hero image
        if (heroActiveImg && styleImg) {
          heroActiveImg.style.opacity = '0.3';
          setTimeout(function () {
            heroActiveImg.src = styleImg;
            heroActiveImg.style.opacity = '1';
          }, 120);
        }

        if (heroActiveThumb && styleImg) {
          heroActiveThumb.src = styleImg;
        }

        if (stickyActiveImg && styleImg) {
          stickyActiveImg.src = styleImg;
        }

        // Scroll gallery to slide 1
        if (viewport) {
          viewport.scrollTo({ left: 0, behavior: 'smooth' });
        }
      });
    });
  }

  /* ==========================================================================
     3. QUANTITY BREAKS: TIER SELECTION & VISUAL STATE
     ========================================================================== */
  function initQuantityBreaks() {
    const bundleCards = document.querySelectorAll('.pdp-bundle-card');
    const atcPriceEl = document.getElementById('atcDynamicPrice');
    const stickyTierNameEl = document.getElementById('stickyTierName');
    const stickyPriceEl = document.getElementById('stickyPrice');
    const stickyBadgeEl = document.getElementById('stickyBadge');

    bundleCards.forEach(function (card) {
      card.addEventListener('click', function (e) {
        // If clicking directly on a chip, ignore card click handler as chip handles it
        if (e.target.closest('.pdp-style-chip')) {
          return;
        }
        const tier = parseInt(card.dataset.tier, 10);
        selectTier(tier);
      });

      const radio = card.querySelector('.pdp-bundle-radio');
      if (radio) {
        radio.addEventListener('change', function () {
          const tier = parseInt(card.dataset.tier, 10);
          selectTier(tier);
        });
      }
    });

    window.selectTier = selectTier;

    function selectTier(tier) {
      state.selectedTier = tier;

      bundleCards.forEach(function (c) {
        const isTarget = parseInt(c.dataset.tier, 10) === tier;
        c.classList.toggle('selected', isTarget);
        const radio = c.querySelector('.pdp-bundle-radio');
        if (radio) radio.checked = isTarget;
      });

      // Update native form input
      const nativeInput = document.getElementById('dualheatNativeVariantId');
      if (nativeInput) {
        nativeInput.value = (tier === 1 ? state.soloStyleId : state.tier2Pair1Id);
      }

      // Update hero image to match selected tier active style
      const heroActiveImg = document.getElementById('pdpHeroActiveImage');
      const heroActiveThumb = document.getElementById('pdpHeroActiveThumb');
      const stickyActiveImg = document.getElementById('stickyActiveImg');
      let targetImg = '';
      if (tier === 1) {
        targetImg = state.soloStyleImage;
      } else if (tier === 2) {
        targetImg = state.tier2Pair1Image;
      }
      if (targetImg) {
        if (heroActiveImg) heroActiveImg.src = targetImg;
        if (heroActiveThumb) heroActiveThumb.src = targetImg;
        if (stickyActiveImg) stickyActiveImg.src = targetImg;
      }

      // Dynamically calculate tier pricing and savings from DOM or state
      const getTierData = function (t) {
        const card = document.getElementById('bundleTier' + t);
        let price = '';
        let compare = '';
        if (card) {
          const pEl = card.querySelector('.pdp-bundle-price');
          const cEl = card.querySelector('.pdp-bundle-compare');
          if (pEl) price = pEl.textContent.trim();
          if (cEl) compare = cEl.textContent.trim();
        }
        const fallback = state.tierPricing[t] || {};
        price = price || fallback.price || '$44.99';

        const pNum = parseFloat(price.replace(/[^0-9.]/g, '')) || 0;
        const cNum = parseFloat(compare.replace(/[^0-9.]/g, '')) || 0;
        let savings = 0;
        if (cNum > pNum) {
          savings = Math.round(cNum - pNum);
        }

        const label = savings > 0 ? ('Add to Cart \u2014 ' + price + ' (Save $' + savings + ')') : ('Add to Cart \u2014 ' + price);
        const stickyBadge = savings > 0 ? ('Save $' + savings) : '';

        return {
          price: price,
          compare: compare,
          savings: savings,
          label: label,
          stickyName: fallback.stickyName || ('Tier ' + t),
          stickyPrice: price,
          stickyBadge: stickyBadge
        };
      };

      // Update CTA labels and sticky bar dynamically
      const tierData = getTierData(tier);
      if (tierData) {
        if (atcPriceEl) atcPriceEl.textContent = tierData.label;
        if (stickyTierNameEl) stickyTierNameEl.textContent = tierData.stickyName;
        if (stickyPriceEl) stickyPriceEl.textContent = tierData.stickyPrice;
        if (stickyBadgeEl) {
          if (tierData.stickyBadge) {
            stickyBadgeEl.textContent = tierData.stickyBadge;
            stickyBadgeEl.style.display = 'inline-block';
          } else {
            stickyBadgeEl.style.display = 'none';
          }
        }
      }
    }

    // Initialize Default View (Tier 2 preselected)
    selectTier(2);
  }

  /* ==========================================================================
     4. STICKY AT-THE-BOTTOM CONVERSION BAR
     ========================================================================== */
  function initStickyBar() {
    const stickyBar = document.getElementById('pdpStickyBar');
    const stickyAtcBtn = document.getElementById('stickyAtcBtn');
    const mainSection = document.getElementById('dualheat-offer');

    if (!stickyBar || !mainSection) return;

    window.addEventListener('scroll', function () {
      const rect = mainSection.getBoundingClientRect();
      // Show sticky bar once user scrolls past the middle of the main offer
      const isPast = rect.top < -150;
      stickyBar.classList.toggle('active', isPast);
      stickyBar.classList.toggle('visible', isPast);
    }, { passive: true });

    if (stickyAtcBtn) {
      stickyAtcBtn.addEventListener('click', function (e) {
        e.preventDefault();
        handleAddToCart();
      });
    }
  }

  /* ==========================================================================
     5. BANNER SHOP TIER 3 TRIGGER
     ========================================================================== */
  function initBannerTrigger() {
    const bannerBtn = document.getElementById('bannerShopTier3Btn');
    const flyerLink = document.getElementById('flyerBannerLink');

    function selectTier3AndScroll() {
      const tier3Card = document.getElementById('bundleTier3');
      if (tier3Card) {
        tier3Card.click();
      }
      const target = document.getElementById('dualheat-offer');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth' });
      }
    }

    if (bannerBtn) {
      bannerBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        selectTier3AndScroll();
      });
    }

    if (flyerLink) {
      flyerLink.addEventListener('click', function (e) {
        e.preventDefault();
        selectTier3AndScroll();
      });
      flyerLink.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          selectTier3AndScroll();
        }
      });
    }
  }

  /* ==========================================================================
     6. CART AJAX API (/cart/add.js) & SHOPIFY NATIVE CHECKOUT
     ========================================================================== */
  function initCartAddAction() {
    const mainAtcBtn = document.getElementById('pdpMainAtcButton');
    const nativeForm = document.getElementById('dualheatNativeForm');

    if (nativeForm) {
      nativeForm.addEventListener('submit', function (e) {
        e.preventDefault();
        handleAddToCart();
      });
    }

    if (mainAtcBtn) {
      mainAtcBtn.addEventListener('click', function (e) {
        e.preventDefault();
        handleAddToCart();
      });
    }
  }

  async function handleAddToCart() {
    const mainAtcBtn = document.getElementById('pdpMainAtcButton');
    const stickyAtcBtn = document.getElementById('stickyAtcBtn');

    if (mainAtcBtn) {
      mainAtcBtn.disabled = true;
      mainAtcBtn.style.opacity = '0.75';
    }
    if (stickyAtcBtn) {
      stickyAtcBtn.disabled = true;
      stickyAtcBtn.style.opacity = '0.75';
    }

    // Ensure we have resolved real store variants
    if (!state.tier2Pair1Id || state.tier2Pair1Id.startsWith('4501') || state.allVariantIds.length === 0 || state.allVariantIds[0].startsWith('4501')) {
      await resolveRealStoreProduct();
    }

    // Fallback: check DOM active chips for valid variant IDs
    if (!state.soloStyleId || state.soloStyleId.startsWith('4501')) {
      const activeSolo = document.querySelector('.pdp-style-chip.active[data-group="solo"]');
      const id = activeSolo?.getAttribute('data-variant-id');
      if (id && !id.startsWith('4501') && id.length >= 6) state.soloStyleId = id;
    }
    if (!state.tier2Pair1Id || state.tier2Pair1Id.startsWith('4501')) {
      const activeP1 = document.querySelector('.pdp-style-chip.active[data-group="pair1"]');
      const id = activeP1?.getAttribute('data-variant-id');
      if (id && !id.startsWith('4501') && id.length >= 6) state.tier2Pair1Id = id;
    }
    if (!state.tier2Pair2Id || state.tier2Pair2Id.startsWith('4501')) {
      const activeP2 = document.querySelector('.pdp-style-chip.active[data-group="pair2"]');
      const id = activeP2?.getAttribute('data-variant-id');
      if (id && !id.startsWith('4501') && id.length >= 6) state.tier2Pair2Id = id;
    }

    // Ultimate fallback if still missing: use first available variant ID
    const fallbackId = state.allVariantIds[0] || state.tier2Pair1Id || state.soloStyleId;
    if (!state.soloStyleId) state.soloStyleId = fallbackId;
    if (!state.tier2Pair1Id) state.tier2Pair1Id = fallbackId;
    if (!state.tier2Pair2Id) state.tier2Pair2Id = state.allVariantIds[1] || fallbackId;

    // Build payload according to selected tier
    let items = [];

    if (state.selectedTier === 1) {
      // Tier 1: Solo Explorer (1 Pair / 2 Pods)
      items = [
        {
          id: state.soloStyleId,
          quantity: 1,
          properties: {
            'Bundle': 'Solo Explorer (1 Pair / 2 Pods)',
            'Style': state.soloStyleName
          }
        }
      ];
    } else if (state.selectedTier === 2) {
      // Tier 2: Couples Pack (2 Pairs / 4 Pods)
      const var1 = state.tier2Pair1Id;
      const var2 = state.tier2Pair2Id;

      if (var1 === var2) {
        items = [
          {
            id: var1,
            quantity: 2,
            properties: {
              'Bundle': 'Couples Pack (2 Pairs / 4 Pods)',
              'Style': state.tier2Pair1Name + ' (Both Pairs)',
              'Pair 1 Style': state.tier2Pair1Name,
              'Pair 2 Style': state.tier2Pair2Name
            }
          }
        ];
      } else {
        items = [
          {
            id: var1,
            quantity: 1,
            properties: {
              'Bundle': 'Couples Pack (2 Pairs / 4 Pods)',
              'Pair': 'Pair 1 of 2',
              'Style': state.tier2Pair1Name,
              'Pair 1 Style': state.tier2Pair1Name,
              'Pair 2 Style': state.tier2Pair2Name
            }
          },
          {
            id: var2,
            quantity: 1,
            properties: {
              'Bundle': 'Couples Pack (2 Pairs / 4 Pods)',
              'Pair': 'Pair 2 of 2',
              'Style': state.tier2Pair2Name,
              'Pair 1 Style': state.tier2Pair1Name,
              'Pair 2 Style': state.tier2Pair2Name
            }
          }
        ];
      }
    } else if (state.selectedTier === 3) {
      // Tier 3: Complete Family Collector Set (All 5 Styles / 10 Pods)
      const styleNames = [
        'Midnight Black (Bulldog)',
        'Arctic White (Bulldog)',
        'Warm Peach (Bear)',
        'Midnight Black (Bear)',
        'Arctic White (Bear)'
      ];

      if (state.allVariantIds.length >= 5) {
        items = state.allVariantIds.slice(0, 5).map(function (varId, idx) {
          return {
            id: varId,
            quantity: 1,
            properties: {
              'Bundle': 'Complete Family Collector Set (5 Pairs / 10 Pods)',
              'Edition': styleNames[idx] || ('Collector Style ' + (idx + 1) + ' of 5'),
              'Included Styles': 'All 5 Collector Styles Included'
            }
          };
        });
      } else {
        items = [
          {
            id: fallbackId,
            quantity: 5,
            properties: {
              'Bundle': 'Complete Family Collector Set (5 Pairs / 10 Pods)',
              'Includes': 'All 5 collector styles (10 Pods + 5 Gift Boxes)'
            }
          }
        ];
      }
    }

    // Verify valid ID exists before submitting to Shopify
    const hasValidIds = items.every(function (item) {
      return item.id && !item.id.toString().startsWith('4501') && item.id.toString().length >= 6;
    });

    if (!hasValidIds) {
      alert('Product variant not found. Please ensure the DualHeat product is published to the Online Store channel in your Shopify Admin (Products > Active).');
      if (mainAtcBtn) { mainAtcBtn.disabled = false; mainAtcBtn.style.opacity = '1'; }
      if (stickyAtcBtn) { stickyAtcBtn.disabled = false; stickyAtcBtn.style.opacity = '1'; }
      return;
    }

    // Prepare Section Rendering request so Dawn's cart drawer updates natively
    const payload = {
      items: items,
      sections: 'cart-drawer,cart-icon-bubble'
    };

    const routes = window.routes || { cart_add_url: '/cart/add.js', cart_url: '/cart' };
    const cartAddUrl = routes.cart_add_url || '/cart/add.js';

    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    })
      .then(async function (response) {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.description || data.message || 'Cart add failed');
        }
        return data;
      })
      .then(async function (data) {
        // 1. Refresh & open Dawn's native cart drawer preview
        await updateDawnCartDrawer(data);
        openCartDrawer();

        // 2. Refresh cart bubble count
        refreshCartBubble();

        // 3. Dispatch PubSub event for any Dawn components listening
        if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'dualheat-bundle',
            cartData: data
          });
        }
      })
      .catch(function (err) {
        console.error('Error adding bundle to cart:', err);
        // Fallback: submit native HTML form
        const nativeForm = document.getElementById('dualheatNativeForm');
        if (nativeForm) {
          nativeForm.submit();
        } else {
          window.location.href = routes.cart_url || '/cart';
        }
      })
      .finally(function () {
        if (mainAtcBtn) {
          mainAtcBtn.disabled = false;
          mainAtcBtn.style.opacity = '1';
        }
        if (stickyAtcBtn) {
          stickyAtcBtn.disabled = false;
          stickyAtcBtn.style.opacity = '1';
        }
      });
  }

  async function updateDawnCartDrawer(data) {
    const routes = window.routes || { cart_url: '/cart' };
    const cartUrl = routes.cart_url || '/cart';

    let drawerHtml = '';
    let bubbleHtml = '';

    if (data && data.sections) {
      drawerHtml = data.sections['cart-drawer'] || '';
      bubbleHtml = data.sections['cart-icon-bubble'] || '';
    }

    // When multi-item bundle is posted to /cart/add.js, Shopify returns { items: [...] } without sections.
    // Fetch the rendered cart-drawer section directly to ensure all items, options, properties, and discounts appear.
    if (!drawerHtml) {
      try {
        const res = await fetch(`${cartUrl}?section_id=cart-drawer`);
        if (res.ok) {
          drawerHtml = await res.text();
        }
      } catch (err) {
        console.error('Failed to fetch cart drawer section:', err);
      }
    }

    if (drawerHtml) {
      const drawerElement = document.querySelector('cart-drawer');
      const parsed = new DOMParser().parseFromString(drawerHtml, 'text/html');
      const newInner = parsed.querySelector('#CartDrawer');
      const oldInner = document.getElementById('CartDrawer');

      if (newInner && oldInner) {
        oldInner.innerHTML = newInner.innerHTML;
      }

      if (drawerElement) {
        drawerElement.classList.remove('is-empty');
        drawerElement.querySelector('.drawer__inner')?.classList.remove('is-empty');
        drawerElement.querySelector('cart-drawer-items')?.classList.remove('is-empty');
        drawerElement.querySelector('.drawer__inner-empty')?.remove();

        // Re-attach close listeners on dynamic overlay
        const overlay = drawerElement.querySelector('#CartDrawer-Overlay');
        if (overlay) {
          overlay.addEventListener('click', function () {
            if (typeof drawerElement.close === 'function') {
              drawerElement.close();
            } else {
              drawerElement.classList.remove('active', 'animate');
              document.body.classList.remove('overflow-hidden');
            }
          });
        }
      }
    }

    if (bubbleHtml) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) {
        const parsedBubble = new DOMParser().parseFromString(bubbleHtml, 'text/html');
        const newBubble = parsedBubble.getElementById('cart-icon-bubble');
        if (newBubble) {
          bubble.innerHTML = newBubble.innerHTML;
        }
      }
    }
  }

  function refreshCartBubble() {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && cart) {
          const countSpan = bubble.querySelector('.cart-count-bubble span');
          if (countSpan) {
            countSpan.textContent = cart.item_count;
          }
        }
      })
      .catch(function () { return null; });
  }

  function openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      const routes = window.routes || { cart_url: '/cart' };
      window.location.href = routes.cart_url || '/cart';
      return;
    }

    cartDrawer.classList.remove('is-empty');
    cartDrawer.querySelector('.drawer__inner')?.classList.remove('is-empty');
    cartDrawer.querySelector('cart-drawer-items')?.classList.remove('is-empty');
    cartDrawer.querySelector('.drawer__inner-empty')?.remove();

    if (typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else {
      setTimeout(function () {
        cartDrawer.classList.add('animate', 'active');
        document.body.classList.add('overflow-hidden');
      }, 50);
    }
  }
})();
